/* eslint-disable prettier/prettier */
import { ValueLanguage, ConstantLanguage } from "../assets/languages/langs";

/**
 * Проверка и нормализация телефонов парка.
 *
 * Международный номер из таблицы (38, 375, 373, 994, 995, 90, 48) принимаем
 * на любом парке. Локальный набор (0…, 80…, NSN без кода страны) — только
 * правилами страны этого парка. Город не определяем.
 *
 * Источники кодов: vdmt.com.ua, minfin.com.ua, phonePL, mold.su, wiki AZ.
 */

export const defaultCountryCodePhone: ValueLanguage = ConstantLanguage.UK;

export enum CodePhone {
	UK = "38",
	AZE = "994",
	PLN = "48",
	MD = "373",
	GE = "995",
	TR = "90",
	BY = "375",
}

export type PhoneCountry =
	| "az"
	| "uk"
	| "pl"
	| "md"
	| "ge"
	| "tr"
	| "ka"
	| "ro"
	| "ua"
	| "by"
	| ValueLanguage;

/** Страна парка совпадает с одним из id правила (`uk` и `ua` — одно правило). */
const parkIs = (
	country: PhoneCountry | undefined,
	...ids: string[]
): boolean => Boolean(country && ids.includes(country));

const alt = (codes: readonly string[]): string => codes.join("|");

/** Полный международный номер: код страны + NDC + хвост фиксированной длины. */
const internationalRegex = (
	callingCode: string,
	ndcs: readonly string[],
	rest: number,
): RegExp =>
	new RegExp(`^\\+?${callingCode}(?:${alt(ndcs)})\\d{${rest}}$`);

/** Локальный номер без кода страны: NDC + тот же хвост. */
const localNdcRegex = (ndcs: readonly string[], rest: number): RegExp =>
	new RegExp(`^\\+?(?:${alt(ndcs)})\\d{${rest}}$`);

/** Белорусские мобильные DEF: life:) 25, A1/MTS 29, MTS 33, A1 44 */
const BY_MOBILE_OPERATORS = "(25|29|33|44)";
const BY_MOBILE_LOCAL_10 = new RegExp(`^0${BY_MOBILE_OPERATORS}\\d{7}$`);
const BY_MOBILE_LOCAL_11 = new RegExp(`^80${BY_MOBILE_OPERATORS}\\d{7}$`);
/** 033/044 как 10 цифр с 0 пересекаются с Украиной — здесь только 025/029. */
const BY_MOBILE_LOCAL_10_NO_UA_COLLISION = /^(025|029)\d{7}$/;

/** Белорусские городские: зоны областей, конкретный город не определяем */
const BY_LANDLINE_ZONES = "(15|16|17|21|22|23)";
const BY_LANDLINE_LOCAL_10 = new RegExp(`^0${BY_LANDLINE_ZONES}\\d{7}$`);
const BY_LANDLINE_LOCAL_11 = new RegExp(`^80${BY_LANDLINE_ZONES}\\d{7}$`);
const BY_INTERNATIONAL = new RegExp(
	`^\\+?375(?:${BY_MOBILE_OPERATORS}|${BY_LANDLINE_ZONES})\\d{7}$`,
);
const BY_LOCAL_9 = new RegExp(
	`^(?:${BY_MOBILE_OPERATORS}|${BY_LANDLINE_ZONES})\\d{7}$`,
);

/** Молдова + Приднестровье: NSN ровно 8 цифр, первая 2–7. 8xx/9xx не берём. */
const MD_NSN = /^[2-7]\d{7}$/;
const MD_LOCAL_0 = /^0[2-7]\d{7}$/;
const MD_INTERNATIONAL = /^\+?373[2-7]\d{7}$/;

/** Украина: ведущий 0 входит в NDC, при склейке не снимаем. */
const UK_NDC = [
	"020", // 2
	"039", // 3
	"031",
	"032",
	"033",
	"034",
	"035",
	"036",
	"037",
	"038",
	"041", // 4
	"042",
	"043",
	"044",
	"045",
	"046",
	"047",
	"048",
	"050", // 5
	"051",
	"052",
	"053",
	"054",
	"055",
	"056",
	"057",
	"066", // 6
	"061",
	"062",
	"063",
	"064",
	"065",
	"067",
	"068",
	"073", // 7
	"070",
	"075",
	"077",
	"700",
	"089", // 8
	"900", // 9
	"090",
	"091",
	"092",
	"093",
	"094",
	"095",
	"096",
	"097",
	"098",
	"099",
] as const;

/** Азербайджан: домашний набор с 0, в международном 0 снимаем. */
const AZ_NDC0 = ["050", "051", "055", "077", "070"] as const;
const AZ_NDC = ["50", "51", "55", "77", "70"] as const;

/** Грузия: 032/034 городские, остальное мобильные. Ведущий 0 у 032 не снимаем. */
const GE_NDC = [
	"032",
	"034",
	"500",
	"501",
	"502",
	"503",
	"504",
	"505",
	"506",
	"507",
	"508",
	"509",
	"510",
	"511",
	"512",
	"513",
	"514",
	"515",
	"516",
	"517",
	"518",
	"519",
	"520",
	"521",
	"522",
	"523",
	"524",
	"525",
	"526",
	"527",
	"528",
	"529",
	"530",
	"531",
	"532",
	"533",
	"534",
	"535",
	"536",
	"537",
	"538",
	"539",
	"540",
	"541",
	"542",
	"543",
	"544",
	"545",
	"546",
	"547",
	"548",
	"549",
	"551",
	"555",
	"557",
	"558",
	"559",
	"568",
	"570",
	"571",
	"574",
	"577",
	"578",
	"579",
	"595",
	"596",
	"597",
	"598",
	"599",
] as const;

/** Турция: мобильные 5xx. Ведущий 0 не клеим (0532… остаётся как есть). */
const TR_NDC = [
	"501",
	"502",
	"503",
	"504",
	"505",
	"506",
	"507",
	"508",
	"509",
	"530",
	"531",
	"532",
	"533",
	"534",
	"535",
	"536",
	"537",
	"538",
	"539",
	"540",
	"541",
	"542",
	"543",
	"544",
	"545",
	"546",
	"547",
	"548",
	"549",
	"550",
	"551",
	"552",
	"553",
	"554",
	"555",
	"556",
	"557",
	"558",
	"559",
] as const;

/** Польша: международный набор без 26/39/47; локально они ещё принимаются. */
const PL_INTL_NDC = [
	"45",
	"50",
	"51",
	"53",
	"57",
	"60",
	"66",
	"69",
	"72",
	"73",
	"78",
	"79",
	"88",
] as const;

const PL_LOCAL_NDC = ["26", "39", "47", ...PL_INTL_NDC] as const;

/**
 * Как клеить код страны к локальному вводу.
 * `codes` — NDC из списка; `by` / `md` — свои длины и снятие 8/0.
 */
type GlueKind = "codes" | "by" | "md";

interface PhoneRule {
	/** Коды парка, для которых действует локальная проверка (`ua` = `uk`). */
	ids: readonly string[];
	/** Международный префикс: по нему принимаем роуминг на любом парке. */
	prefixes: readonly string[];
	/** Полный международный номер. */
	regex: RegExp;
	/** Локальный NSN без кода страны. */
	localRegex: RegExp;
	/** Доп. домашние форматы: 0…, 80…, короткий 050/070. */
	extraLocal?: readonly RegExp[];
	/** Префиксы, при которых `countryCodePhone` клеит код страны. */
	localPrefixes?: readonly string[];
	/** Код страны для склейки (`38`, `375`, …). */
	callingCode?: string;
	/** Снять ведущий 0 перед кодом страны (Азербайджан). */
	stripLeading0?: boolean;
	glue?: GlueKind;
}

const PHONE_RULES: PhoneRule[] = [
	{
		ids: ["uk", "ua"], // Украина: 0 оставляем, сразу 38 + набор
		prefixes: [CodePhone.UK],
		regex: internationalRegex(CodePhone.UK, UK_NDC, 7),
		localRegex: localNdcRegex(UK_NDC, 7),
		// Старое послабление yup: 050/070 с 9–10 цифрами.
		extraLocal: [/^(050|070)\d{6,7}$/],
		localPrefixes: UK_NDC,
		callingCode: CodePhone.UK,
		glue: "codes",
	},
	{
		ids: ["az"], // Азербайджан: 055… -> 99455…
		prefixes: [CodePhone.AZE],
		regex: internationalRegex(CodePhone.AZE, AZ_NDC, 7),
		localRegex: localNdcRegex(AZ_NDC, 7),
		extraLocal: [new RegExp(`^(?:${alt(AZ_NDC0)})\\d{6,7}$`)],
		localPrefixes: AZ_NDC0,
		callingCode: CodePhone.AZE,
		stripLeading0: true,
		glue: "codes",
	},
	{
		ids: ["ge", "ka"], // Грузия; `ka` — тот же парк
		prefixes: [CodePhone.GE],
		regex: internationalRegex(CodePhone.GE, GE_NDC, 6),
		localRegex: localNdcRegex(GE_NDC, 6),
		localPrefixes: GE_NDC,
		callingCode: CodePhone.GE,
		glue: "codes",
	},
	{
		ids: ["tr"], // Турция: 90, не путать с Грузией 995
		prefixes: [CodePhone.TR],
		regex: internationalRegex(CodePhone.TR, TR_NDC, 7),
		localRegex: localNdcRegex(TR_NDC, 7),
		localPrefixes: TR_NDC,
		callingCode: CodePhone.TR,
		glue: "codes",
	},
	{
		ids: ["pl"], // Польша
		prefixes: [CodePhone.PLN],
		regex: internationalRegex(CodePhone.PLN, PL_INTL_NDC, 7),
		localRegex: localNdcRegex(PL_INTL_NDC, 7),
		extraLocal: [/^(26|39|47)\d{7}$/], // локально клеим, в 48… не входят
		localPrefixes: PL_LOCAL_NDC,
		callingCode: CodePhone.PLN,
		glue: "codes",
	},
	{
		ids: ["md", "ro"], // `ro` — заглушка той же схемы 373
		prefixes: [CodePhone.MD],
		regex: MD_INTERNATIONAL,
		localRegex: MD_NSN,
		extraLocal: [MD_LOCAL_0],
		glue: "md",
	},
	{
		ids: ["by"], // Беларусь: 8/0 снимаем только на полном номере
		prefixes: [CodePhone.BY],
		regex: BY_INTERNATIONAL,
		localRegex: BY_LOCAL_9,
		extraLocal: [
			BY_MOBILE_LOCAL_10_NO_UA_COLLISION,
			BY_LANDLINE_LOCAL_10,
			BY_MOBILE_LOCAL_11,
			BY_LANDLINE_LOCAL_11,
		],
		glue: "by",
	},
];

/** Правило парка по `countryTaxiService`; без страны — только международный формат. */
const findParkRule = (country?: PhoneCountry): PhoneRule | undefined =>
	PHONE_RULES.find((rule) => parkIs(country, ...rule.ids));

/** Склеить код страны, если набор начинается с NDC этой страны. */
const glueByCodes = (phone: string, rule: PhoneRule): string => {
	const prefixes = rule.localPrefixes || [];
	if (!prefixes.some((prefix) => phone.startsWith(prefix))) {
		return phone;
	}

	const rest =
		rule.stripLeading0 && phone.startsWith("0") ? phone.slice(1) : phone;

	return `${rule.callingCode}${rest}`;
};

/** Беларусь: 80… / 0… / 9 цифр NSN -> 375…, иначе не трогаем. */
const glueBelarus = (phone: string): string => {
	const digits = phone.replace(/\D/g, "");

	if (BY_INTERNATIONAL.test(digits)) {
		return digits;
	}
	if (BY_MOBILE_LOCAL_11.test(digits) || BY_LANDLINE_LOCAL_11.test(digits)) {
		return `${CodePhone.BY}${digits.slice(2)}`;
	}
	if (BY_MOBILE_LOCAL_10.test(digits) || BY_LANDLINE_LOCAL_10.test(digits)) {
		return `${CodePhone.BY}${digits.slice(1)}`;
	}
	if (BY_LOCAL_9.test(digits)) {
		return `${CodePhone.BY}${digits}`;
	}

	return phone;
};

/** Молдова / ПМР: 0 + 8 цифр или 8 цифр NSN -> 373… */
const glueMoldova = (phone: string): string => {
	const digits = phone.replace(/\D/g, "");

	if (MD_INTERNATIONAL.test(digits)) {
		return digits;
	}
	if (MD_LOCAL_0.test(digits)) {
		return `${CodePhone.MD}${digits.slice(1)}`;
	}
	if (MD_NSN.test(digits)) {
		return `${CodePhone.MD}${digits}`;
	}

	return phone;
};

/** Международный префикс из таблицы — без страны парка (роуминг). */
export const validatePhonePrefix = (phone: string): boolean => {
	if (!phone) return false;

	const cleanPhone = phone.replace(/\s|-/g, "");

	return PHONE_RULES.some((rule) => {
		if (!rule.prefixes.some((p) => cleanPhone.startsWith(p))) {
			return false;
		}
		return rule.regex.test(cleanPhone);
	});
};

/**
 * Сначала любой международный из таблицы, затем локальные правила парка.
 * Без `country` локальный набор не принимаем.
 */
export const validatePhone = (
	num: string,
	country?: PhoneCountry,
): boolean => {
	if (!num) return false;
	const phone = num.replace(/\D/g, "");

	if (validatePhonePrefix(phone)) return true;

	const rule = findParkRule(country);
	if (!rule) return false;

	if (rule.localRegex.test(phone)) return true;

	return rule.extraLocal?.some((pattern) => pattern.test(phone)) === true;
};

/** Привести локальный набор парка к международному; чужой номер не трогаем. */
export const countryCodePhone = (
	phone: string,
	country: PhoneCountry,
): string => {
	if (!phone) return phone;

	const rule = findParkRule(country);
	if (!rule) return phone;

	if (rule.glue === "by") return glueBelarus(phone);
	if (rule.glue === "md") return glueMoldova(phone);
	if (rule.glue === "codes") return glueByCodes(phone, rule);

	return phone;
};

export default validatePhone;
